import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../utils/appStore";
import { clearItems, removeItem } from "../utils/cartSlice";
import { CartItem } from "../types";
import { ITEM_IMG_CDN_URL } from "../constants";

const CartPage: React.FC = () => {
    const cartItems = useSelector((state: RootState) => state.cart.items);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleRemoveItem = (item: CartItem) => {
        dispatch(removeItem(item));
    };

    const handleClearCart = () => {
        dispatch(clearItems());
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const totalPrice = cartItems.reduce((sum, item) => sum + (item.price || 0), 0);

    const formatPrice = (price: number): string => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
        }).format(price / 100);
    };

    return (
        <div className="pt-16 min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
            <div className="max-w-4xl mx-auto px-6 py-12">
                {/* Back Button */}
                <button
                    onClick={handleGoBack}
                    className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium mb-6 transition-colors"
                >
                    <i className="fa-solid fa-arrow-left"></i>
                    Back
                </button>

                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Shopping Cart</h1>
                        <p className="text-gray-500 dark:text-gray-400">{cartItems.length} items</p>
                    </div>
                    {cartItems.length > 0 && (
                        <button
                            onClick={handleClearCart}
                            className="text-red-500 hover:text-red-600 font-medium transition-colors"
                        >
                            Clear Cart
                        </button>
                    )}
                </div>

                {/* Cart Items */}
                {cartItems.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="text-6xl mb-4">🛒</div>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            Your cart is empty
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400">
                            Add some delicious items from our restaurants!
                        </p>
                    </div>
                ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden transition-colors">
                        {cartItems.map((item, index) => (
                            <div
                                key={`${item.id}-${index}`}
                                className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700 last:border-0"
                            >
                                <div className="flex items-center gap-4">
                                    {item.imageId && (
                                        <img
                                            src={ITEM_IMG_CDN_URL + item.imageId}
                                            alt={item.name}
                                            className="w-20 h-16 object-cover rounded-lg"
                                        />
                                    )}
                                    <div>
                                        <h3 className="font-semibold text-gray-900 dark:text-white">{item.name}</h3>
                                        <p className="text-orange-500 font-medium">
                                            {formatPrice(item.price)}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleRemoveItem(item)}
                                    className="text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 px-4 py-2 rounded-lg font-medium transition-colors"
                                >
                                    Remove
                                </button>
                            </div>
                        ))}

                        {/* Total */}
                        <div className="p-6 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between">
                            <span className="text-lg font-semibold text-gray-900 dark:text-white">Total</span>
                            <span className="text-2xl font-bold text-orange-500">
                                {formatPrice(totalPrice)}
                            </span>
                        </div>
                    </div>
                )}

                {/* Checkout Button */}
                {cartItems.length > 0 && (
                    <button className="w-full mt-6 bg-orange-500 hover:bg-orange-600 text-white font-semibold py-4 rounded-xl transition-colors">
                        Proceed to Checkout
                    </button>
                )}
            </div>
        </div>
    );
};

export default CartPage;
